// TODO: i18n — see Auth.js for pattern, replace hardcoded strings with t('namespace.key')
import { useState, useRef, useEffect } from 'react'
import { Thermometer, Refrigerator, Droplets, WashingMachine, Wind, Sparkles, Flame, Microwave, RefreshCw, AlarmSmoke, DoorClosed, Wrench, Camera, ShoppingBag, ShoppingCart, Plus, Trash2, ChevronDown } from 'lucide-react'
import { supabase } from '../supabase'
import MaintenanceTaskTile from './MaintenanceTaskTile'
import MaintenanceTaskModal from './MaintenanceTaskModal'
import { compressImage } from '../imageUtils'
import { recordProvenance } from '../utils/photoProvenance'
import { stripExif } from '../utils/stripExif'
import {
  updateApplianceAsset,
  updateApplianceSlot,
  updateAppliancePlacement,
  deleteAppliance as deleteApplianceRow,
} from '../utils/applianceQueries'

const C = {
  bg: '#F2EDE4', card: '#FDFAF6', border: '#DDD4C6',
  amber: '#B8721F', amberLight: '#D4902A', brown: '#2C1D14',
  muted: '#9A7B68', success: '#2E6B4A', danger: '#B03020',
  successBg: '#EBF5EF', dangerBg: '#FDECEA', warningBg: '#FBF3E8',
}

// Calculate next due date from last completed date + interval string
function calcNextDue(lastDate, interval) {
  if (!lastDate || !interval) return null
  const d = new Date(lastDate)
  const i = interval.toLowerCase()
  if (i.includes('every use') || i.includes('each use')) return null // self-managed
  if (i.includes('month') && i.includes('3')) d.setDate(d.getDate() + 90)
  else if (i.includes('month') && i.includes('6')) d.setDate(d.getDate() + 180)
  else if (i.includes('month')) d.setDate(d.getDate() + 30)
  else if (i.includes('90')) d.setDate(d.getDate() + 90)
  else if (i.includes('2 year')) d.setDate(d.getDate() + 730)
  else if (i.includes('year') || i.includes('annual')) d.setDate(d.getDate() + 365)
  else if (i.includes('180')) d.setDate(d.getDate() + 180)
  else if (i.includes('week')) d.setDate(d.getDate() + 7)
  else return null
  return d.toISOString().split('T')[0]
}

function getDueStatus(nextDue) {
  if (!nextDue) return null
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const due = new Date(nextDue)
  const diffDays = Math.ceil((due - today) / (1000 * 60 * 60 * 24))
  if (diffDays < 0) return 'overdue'
  if (diffDays <= 30) return 'soon'
  return 'good'
}

function isSelfManaged(interval) {
  const i = (interval || '').toLowerCase()
  return i.includes('every use') || i.includes('each use')
}

const nationalAverages = {
  'Filter Replacement': { avg: 25 }, 'Coil Cleaning': { avg: 150 },
  'Condensate Drain Flush': { avg: 0 }, 'Full System Inspection': { avg: 120 },
  'Water Filter Replacement': { avg: 45 }, 'Condenser Coil Cleaning': { avg: 0 },
  'Door Seal Inspection': { avg: 0 }, 'Tank Flush & Sediment Drain': { avg: 0 },
  'Anode Rod Inspection': { avg: 180 }, 'Pressure Relief Valve Test': { avg: 0 },
  'Drum Cleaning Cycle': { avg: 12 }, 'Door Gasket Wipe Down': { avg: 0 },
  'Inlet Hose Inspection': { avg: 0 }, 'Lint Trap Cleaning': { avg: 0 },
  'Vent Duct Cleaning': { avg: 130 }, 'Exterior Vent Check': { avg: 0 },
  'Filter Cleaning': { avg: 0 }, 'Spray Arm Cleaning': { avg: 0 },
  'Descaling Treatment': { avg: 12 }, 'Battery Replacement': { avg: 8 },
  'Test & Inspection': { avg: 0 }, 'Full Replacement': { avg: 35 },
  'Lubricate Moving Parts': { avg: 12 }, 'Safety Sensor Test': { avg: 0 },
  'Full Service Inspection': { avg: 100 }, 'Deep Clean': { avg: 0 },
  'Burner Inspection': { avg: 0 }, 'Interior Clean': { avg: 0 },
  'Cleaning & Deodorizing': { avg: 8 }, 'Blade Inspection': { avg: 0 },
  'General Inspection': { avg: 0 },
}

function getCostComparison(taskName, amountPaid) {
  const nat = nationalAverages[taskName]
  if (!nat || nat.avg === 0) return null
  const diff = amountPaid - nat.avg
  if (diff < -5) return { type: 'saved', text: `$${Math.abs(Math.round(diff))} below national avg` }
  if (diff > 5) return { type: 'over', text: `$${Math.round(diff)} above national avg` }
  return { type: 'avg', text: 'Right at national avg' }
}

function getManualUrl(make, model) {
  const clean = (s) => (s || '').replace(/[/\\]/g, ' ').trim()
  const q = encodeURIComponent(`${clean(make)} ${clean(model)} manual filetype:pdf OR site:manualslib.com OR site:manua.ls`)
  return `https://www.google.com/search?q=${q}`
}

function formatDate(dateStr) {
  if (!dateStr) return ''
  try { return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) }
  catch { return dateStr }
}

const S = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(44,29,20,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '20px', fontFamily: 'Georgia, serif',
  },
  modal: {
    background: C.card, borderRadius: '16px', width: '100%',
    maxWidth: '580px', maxHeight: '90vh', overflowY: 'auto',
    boxShadow: '0 8px 40px rgba(44,29,20,0.2)', border: `1px solid ${C.border}`,
  },
  header: { padding: '24px 28px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  title: { fontSize: '22px', fontWeight: '700', color: C.brown },
  closeBtn: { background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: C.muted, padding: '0', lineHeight: 1 },
  body: { padding: '20px 28px 28px' },
  photoBox: {
    width: '100%', height: '200px', borderRadius: '12px',
    background: C.bg, border: `1px solid ${C.border}`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    marginBottom: '20px', overflow: 'hidden', position: 'relative',
  },
  photoImg: { width: '100%', height: '100%', objectFit: 'cover' },
  photoActions: {
    position: 'absolute', bottom: '10px', right: '10px',
    display: 'flex', gap: '6px',
  },
  photoBtn: {
    background: C.card, border: `1px solid ${C.border}`,
    borderRadius: '8px', padding: '6px 12px', fontSize: '11px',
    color: C.muted, cursor: 'pointer', fontFamily: 'Calibri, sans-serif',
  },
  photoUploading: {
    position: 'absolute', inset: 0, background: 'rgba(253,250,246,0.85)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '13px', color: C.amber, fontFamily: 'Calibri, sans-serif',
  },
  detailGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' },
  detailItem: { background: C.bg, borderRadius: '10px', padding: '12px 14px', border: `1px solid ${C.border}` },
  detailLabel: { fontSize: '10px', color: C.muted, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '4px', fontFamily: 'Calibri, sans-serif' },
  detailValue: { fontSize: '14px', fontWeight: '600', color: C.brown },
  detailEmpty: { fontSize: '13px', color: C.muted, fontStyle: 'italic' },
  identityBadgeRow: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' },
  identityBadge: {
    border: `1px solid ${C.amberLight}`, background: C.card, color: C.brown,
    fontFamily: 'Calibri, sans-serif', fontSize: '14px',
    padding: '4px 10px', borderRadius: '12px',
    display: 'inline-block', lineHeight: '1.2', whiteSpace: 'nowrap',
  },
  sectionTitle: { fontSize: '11px', color: C.muted, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '12px', fontFamily: 'Calibri, sans-serif' },
  divider: { borderTop: `1px solid ${C.border}`, margin: '20px 0' },
  scheduleRow: (hovered, completed) => ({
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '11px 10px', borderBottom: `1px solid ${C.border}`,
    borderRadius: '8px', margin: '0 -10px',
    background: completed ? C.successBg : hovered ? '#FBF3E8' : 'transparent',
    transition: 'background 0.15s', opacity: completed ? 0.85 : 1,
  }),
  checkBtn: (completed) => ({
    width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0,
    border: `2px solid ${completed ? C.success : C.border}`,
    background: completed ? C.success : 'transparent',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'all 0.2s',
  }),
  tag: (type) => {
    const colors = { auto_order: { bg: '#FBF3E8', color: '#B8721F' }, contractor: { bg: '#EBF5EF', color: '#2E6B4A' }, diy: { bg: '#F2EDE4', color: '#9A7B68' } }
    const c = colors[type] || colors.diy
    return { fontSize: '10px', padding: '3px 10px', borderRadius: '20px', background: c.bg, color: c.color, fontFamily: 'Calibri, sans-serif', border: `1px solid ${c.color}44`, whiteSpace: 'nowrap' }
  },
  partCost: { fontSize: '10px', color: C.success, fontFamily: 'Calibri, sans-serif', fontWeight: '600', marginTop: '2px' },
  howToHint: { fontSize: '10px', color: C.amber, fontFamily: 'Calibri, sans-serif', marginTop: '2px' },
  notesBox: {
    width: '100%', background: C.bg, border: `1px solid ${C.border}`,
    borderRadius: '8px', padding: '11px 14px', fontSize: '14px',
    fontFamily: 'Georgia, serif', color: C.brown, boxSizing: 'border-box',
    outline: 'none', resize: 'vertical', minHeight: '80px', marginBottom: '8px',
  },
  saveNotesBtn: { background: 'transparent', border: `1px solid ${C.amber}`, borderRadius: '8px', padding: '7px 16px', fontSize: '12px', color: C.amber, cursor: 'pointer', fontFamily: 'Georgia, serif', letterSpacing: '1px' },
  btnRow: { display: 'flex', gap: '10px', marginTop: '20px' },
  editBtn: { flex: 1, background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px', fontSize: '13px', color: C.muted, cursor: 'pointer', fontFamily: 'Georgia, serif', letterSpacing: '1px', textTransform: 'uppercase' },
  replaceBtn: { flex: 1, background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`, border: 'none', borderRadius: '10px', padding: '12px', fontSize: '13px', color: C.card, cursor: 'pointer', fontFamily: 'Georgia, serif', letterSpacing: '1px', textTransform: 'uppercase', fontWeight: '700' },
  deleteBtn: { background: 'transparent', border: `1px solid ${C.danger}44`, borderRadius: '10px', padding: '12px 16px', fontSize: '13px', color: C.danger, cursor: 'pointer', fontFamily: 'Georgia, serif', letterSpacing: '1px', textTransform: 'uppercase' },
  input: { width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 14px', fontSize: '14px', fontFamily: 'Georgia, serif', color: C.brown, boxSizing: 'border-box', outline: 'none', marginBottom: '12px' },
  alert: (type) => ({
    borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px', fontFamily: 'Calibri, sans-serif',
    background: type === 'error' ? C.dangerBg : C.successBg,
    border: `1px solid ${type === 'error' ? C.danger : C.success}44`,
    color: type === 'error' ? C.danger : C.success,
  }),
  statusBadge: (status) => ({
    display: 'inline-block', fontSize: '11px', padding: '3px 10px', borderRadius: '20px', fontFamily: 'Calibri, sans-serif',
    background: status === 'good' ? '#EBF5EF' : status === 'due' ? '#FBF3E8' : '#FDECEA',
    color: status === 'good' ? C.success : status === 'due' ? C.amber : C.danger,
    border: `1px solid ${status === 'good' ? C.success : status === 'due' ? C.amber : C.danger}44`,
  }),
  costModalOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(44,29,20,0.6)',
    display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    zIndex: 2000, fontFamily: 'Georgia, serif',
  },
  costModal: {
    background: C.card, borderRadius: '16px 16px 0 0', width: '100%',
    maxWidth: '540px', padding: '28px',
    boxShadow: '0 -8px 40px rgba(44,29,20,0.2)', border: `1px solid ${C.border}`,
  },
  costInput: {
    flex: 1, background: C.bg, border: `1px solid ${C.border}`,
    borderRadius: '8px', padding: '11px 14px', fontSize: '15px',
    fontFamily: 'Georgia, serif', color: C.brown, boxSizing: 'border-box', outline: 'none',
  },
  costLabel: { fontSize: '10px', color: C.muted, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '5px', display: 'block', fontFamily: 'Calibri, sans-serif' },
  natAvgBadge: (type) => ({
    fontSize: '12px', padding: '6px 12px', borderRadius: '8px', fontFamily: 'Calibri, sans-serif',
    fontWeight: '600', marginBottom: '16px', display: 'inline-block',
    background: type === 'saved' ? C.successBg : type === 'over' ? C.dangerBg : C.warningBg,
    color: type === 'saved' ? C.success : type === 'over' ? C.danger : C.amber,
    border: `1px solid ${type === 'saved' ? C.success : type === 'over' ? C.danger : C.amber}33`,
  }),
  costSaveBtn: { width: '100%', background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`, border: 'none', borderRadius: '10px', padding: '14px', color: C.card, fontSize: '14px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'Georgia, serif' },
  costSkipBtn: { width: '100%', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px', color: C.muted, fontSize: '13px', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'Georgia, serif', marginTop: '10px' },
  costHistoryItem: { padding: '12px 0', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' },
  costHistoryTask: { fontSize: '13px', fontWeight: '600', color: C.brown },
  costHistoryMeta: { fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginTop: '2px' },
  costHistoryComparison: (type) => ({ fontSize: '10px', fontFamily: 'Calibri, sans-serif', marginTop: '2px', textAlign: 'right', color: type === 'saved' ? C.success : type === 'over' ? C.danger : C.amber }),
  costSummaryCard: { background: C.bg, borderRadius: '10px', border: `1px solid ${C.border}`, padding: '14px 16px', marginBottom: '16px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' },
  costSummaryItem: { textAlign: 'center' },
  costSummaryValue: { fontSize: '18px', fontWeight: '700', color: C.brown },
  costSummaryLabel: { fontSize: '10px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginTop: '2px' },
}

const applianceIcons = {
  hvac: Thermometer, refrigerator: Refrigerator, water_heater: Droplets,
  washer: WashingMachine, dryer: Wind, dishwasher: Sparkles,
  oven: Flame, microwave: Microwave, garbage_disposal: RefreshCw,
  smoke_detector: AlarmSmoke, garage_door: DoorClosed, other: Wrench,
}


const maintenanceSchedules = {
  hvac: [
    { task: 'Filter Replacement', interval: 'Every 90 days', type: 'auto_order', partCost: '$15–$40' },
    { task: 'Coil Cleaning', interval: 'Annually — Spring', type: 'contractor' },
    { task: 'Condensate Drain Flush', interval: 'Every 6 months', type: 'diy' },
    { task: 'Full System Inspection', interval: 'Annually — Fall', type: 'contractor' },
  ],
  refrigerator: [
    { task: 'Water Filter Replacement', interval: 'Every 6 months', type: 'auto_order', partCost: '$25–$55' },
    { task: 'Condenser Coil Cleaning', interval: 'Annually', type: 'diy' },
    { task: 'Door Seal Inspection', interval: 'Annually', type: 'diy' },
  ],
  water_heater: [
    { task: 'Tank Flush & Sediment Drain', interval: 'Annually', type: 'diy' },
    { task: 'Anode Rod Inspection', interval: 'Every 2 years', type: 'contractor' },
    { task: 'Pressure Relief Valve Test', interval: 'Annually', type: 'diy' },
  ],
  washer: [
    { task: 'Drum Cleaning Cycle', interval: 'Monthly', type: 'auto_order', partCost: '$8–$15' },
    { task: 'Door Gasket Wipe Down', interval: 'Monthly', type: 'diy' },
    { task: 'Inlet Hose Inspection', interval: 'Annually', type: 'diy' },
  ],
  dryer: [
    { task: 'Lint Trap Cleaning', interval: 'Every use', type: 'diy' },
    { task: 'Vent Duct Cleaning', interval: 'Annually', type: 'contractor' },
    { task: 'Exterior Vent Check', interval: 'Every 6 months', type: 'diy' },
  ],
  dishwasher: [
    { task: 'Filter Cleaning', interval: 'Monthly', type: 'diy' },
    { task: 'Spray Arm Cleaning', interval: 'Every 3 months', type: 'diy' },
    { task: 'Descaling Treatment', interval: 'Every 6 months', type: 'auto_order', partCost: '$8–$18' },
  ],
  smoke_detector: [
    { task: 'Battery Replacement', interval: 'Annually', type: 'auto_order', partCost: '$5–$12' },
    { task: 'Test & Inspection', interval: 'Monthly', type: 'diy' },
    { task: 'Full Replacement', interval: 'Every 10 years', type: 'auto_order', partCost: '$20–$50' },
  ],
  garage_door: [
    { task: 'Lubricate Moving Parts', interval: 'Every 6 months', type: 'auto_order', partCost: '$8–$15' },
    { task: 'Safety Sensor Test', interval: 'Monthly', type: 'diy' },
    { task: 'Full Service Inspection', interval: 'Annually', type: 'contractor' },
  ],
  oven: [
    { task: 'Deep Clean', interval: 'Every 3 months', type: 'diy' },
    { task: 'Burner Inspection', interval: 'Annually', type: 'diy' },
  ],
  microwave: [
    { task: 'Interior Clean', interval: 'Monthly', type: 'diy' },
    { task: 'Filter Replacement', interval: 'Every 6 months', type: 'auto_order', partCost: '$10–$25' },
  ],
  garbage_disposal: [
    { task: 'Cleaning & Deodorizing', interval: 'Monthly', type: 'auto_order', partCost: '$5–$12' },
    { task: 'Blade Inspection', interval: 'Annually', type: 'diy' },
  ],
  other: [{ task: 'General Inspection', interval: 'Annually', type: 'diy' }],
}

// ── INLINE EDITABLE FIELD ───────────────────────────────────────────────────
// Tap a cell to edit. Saves on blur / Enter / date-change. Escape reverts.
// Border flashes green on success, red on error (and reverts the value).
// fieldOwner picks which helper to call (asset/slot/placement) so all three
// tables can be edited from the same component on one screen.
function InlineEditableField({ label, value, placeholder, placementId, fieldName, fieldOwner, inputType = 'text', onSaved }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value || '')
  const [savingState, setSavingState] = useState('idle') // 'idle' | 'saving' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState(null)
  const inputRef = useRef(null)

  // Keep draft in sync with parent updates that happen for other reasons (e.g. rescan)
  useEffect(() => { if (!editing) setDraft(value || '') }, [value, editing])

  const beginEdit = () => {
    setDraft(value || '')
    setEditing(true)
    setSavingState('idle')
    setErrorMsg(null)
    // Autofocus + select on next paint
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus()
        if (inputType !== 'date') inputRef.current.select?.()
      }
    }, 0)
  }

  const commit = async () => {
    const trimmed = (draft || '').trim()
    const original = (value || '').trim()
    if (trimmed === original) { setEditing(false); return }
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('[InlineEditableField] commit', { label, fieldName, fieldOwner, placementId, original, draft: trimmed })
    }
    setSavingState('saving')
    try {
      const updater = fieldOwner === 'slot'
        ? updateApplianceSlot
        : fieldOwner === 'placement'
          ? updateAppliancePlacement
          : updateApplianceAsset
      await updater(supabase, placementId, { [fieldName]: trimmed || null })
      setEditing(false)
      setSavingState('success')
      if (onSaved) onSaved()
      setTimeout(() => setSavingState('idle'), 500)
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.log('[InlineEditableField] commit FAILED', { label, fieldName, error: err?.message || err })
      }
      setErrorMsg('Save failed')
      setDraft(value || '') // revert
      setSavingState('error')
      setTimeout(() => { setSavingState('idle'); setEditing(false); setErrorMsg(null) }, 500)
    }
  }

  const cancel = () => {
    setDraft(value || '')
    setEditing(false)
    setSavingState('idle')
    setErrorMsg(null)
  }

  const flashBorder = savingState === 'success' ? C.success
    : savingState === 'error' ? C.danger
    : C.border

  const cellStyle = {
    ...S.detailItem,
    border: `1px solid ${flashBorder}`,
    transition: 'border-color 0.15s',
    cursor: editing ? 'text' : 'pointer',
  }

  const hasValue = !!(value && String(value).trim())
  const valueStyle = hasValue ? S.detailValue : { ...S.detailValue, ...S.detailEmpty }

  return (
    <div style={cellStyle} onClick={() => !editing && beginEdit()}>
      <div style={S.detailLabel}>{label}</div>
      {editing ? (
        <input
          ref={inputRef}
          type={inputType}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={e => {
            if (e.key === 'Enter' && inputType !== 'date') { e.preventDefault(); inputRef.current?.blur() }
            else if (e.key === 'Escape') { e.preventDefault(); cancel() }
          }}
          style={{
            width: '100%', boxSizing: 'border-box',
            background: 'transparent', border: 'none', padding: 0, margin: 0,
            fontSize: '14px', fontWeight: '600', color: C.brown,
            fontFamily: 'Calibri, sans-serif', outline: 'none',
          }}
        />
      ) : (
        <div style={valueStyle}>{hasValue ? value : (placeholder || 'Tap to add')}</div>
      )}
      {errorMsg && <div style={{ fontSize: '11px', color: C.danger, marginTop: '2px' }}>{errorMsg}</div>}
    </div>
  )
}

export default function ApplianceDetail({ appliance, onClose, onUpdated, onPhotoUpdated, onDeleted }) {
  const DEV = process.env.NODE_ENV === 'development'

  // Resolve the current authenticated user id once for any insert/storage path needing it.
  // The legacy appliance.user_id field is gone; rows are scoped via the home/slot chain.
  const getCurrentUserId = async () => {
    const { data: { session } } = await supabase.auth.getSession()
    return session?.user?.id || null
  }

  const [mode, setMode] = useState('view')
  const [notes, setNotes] = useState(appliance.notes || '')
  const [notesSaved, setNotesSaved] = useState(false)
  const [loading, setLoading] = useState(false)
  const [photoUploading, setPhotoUploading] = useState(false)
  const [alert, setAlert] = useState(null)
  const [manualUrl, setManualUrl] = useState(appliance.manual_url || null)
  const [manualUploading, setManualUploading] = useState(false)
  const [manualLookupStatus, setManualLookupStatus] = useState('idle') // 'idle'|'loading'|'done'
  const [showReplaceField, setShowReplaceField] = useState(false)
  const [pastedUrl, setPastedUrl] = useState('')
  const [imgFailed, setImgFailed] = useState(false)
  const [selectedTask, setSelectedTask] = useState(null)
  const [completedTasks, setCompletedTasks] = useState(appliance.completed_tasks || {})
  const [taskDates, setTaskDates] = useState(appliance.task_dates || {})
  const [costHistory, setCostHistory] = useState(appliance.cost_history || [])
  const stockPhotoUrl = appliance.photo_url || null
  const [photos, setPhotos] = useState(appliance.photos || [])
  // User photos take priority as the cover; fall back to stock/ManualsLib thumbnail
  const coverPhoto = photos.length > 0 ? photos[0] : stockPhotoUrl
  const MAX_PHOTOS = 10

  const [applianceParts, setApplianceParts] = useState([])

  // Build an "Order Parts" handler for a maintenance task — opens Amazon search with affiliate tag
  const getOrderNow = (taskItem) => {
    if (taskItem.type !== 'auto_order') return null
    return () => {
      const makeModel = `${appliance.make || ''} ${appliance.model || ''}`.trim() || displayName
      const q = encodeURIComponent(`${makeModel} ${taskItem.task}`)
      window.open(`https://www.amazon.com/s?k=${q}&tag=casavio-20`, '_blank', 'noopener,noreferrer')
    }
  }
  const [partsLoading, setPartsLoading] = useState(true)
  const [showAddPart, setShowAddPart] = useState(false)
  const [partsCatalog, setPartsCatalog] = useState([])
  const [selectedCatalogPart, setSelectedCatalogPart] = useState(null)
  const [partIntervalDays, setPartIntervalDays] = useState('')
  const [partNextDue, setPartNextDue] = useState('')
  const [partSaving, setPartSaving] = useState(false)

  const [scanStatus, setScanStatus] = useState(appliance.parts_scan_status || null)
  const [scanFound, setScanFound] = useState(appliance.parts_scan_found || 0)
  const [scanDismissed, setScanDismissed] = useState(false)

  const [showCompletedTasks, setShowCompletedTasks] = useState(false)
  const [pendingTask, setPendingTask] = useState(null)
  const [completionDate, setCompletionDate] = useState('')
  const [costAmount, setCostAmount] = useState('')
  const [costProvider, setCostProvider] = useState('')
  const [costNote, setCostNote] = useState('')
  const fileInputRef = useRef(null)
  const manualInputRef = useRef(null)
  const pollRef = useRef(null)

  const [energyStarStatus, setEnergyStarStatus] = useState('loading')
  const [energyStarAttempt, setEnergyStarAttempt] = useState(0)

  const schedule = maintenanceSchedules[appliance.type] || maintenanceSchedules.other
  const AppIcon = applianceIcons[appliance.type] || Wrench
  const displayName = appliance.nickname || appliance.name

  // ── PHOTO GALLERY ─────────────────────────────────────────────────────────
  const handlePhotoClick = () => {
    if (photos.length >= MAX_PHOTOS) {
      setAlert({ type: 'error', msg: `Maximum ${MAX_PHOTOS} photos allowed.` })
      return
    }
    fileInputRef.current?.click()
  }

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 20 * 1024 * 1024) {
      setAlert({ type: 'error', msg: 'Photo must be under 20MB.' }); return
    }
    if (photos.length >= MAX_PHOTOS) {
      setAlert({ type: 'error', msg: `Maximum ${MAX_PHOTOS} photos allowed.` }); return
    }

    setPhotoUploading(true)
    setAlert(null)

    try {
      const userId = await getCurrentUserId()
      const stripped = await stripExif(file)
      const compressed = await compressImage(stripped)
      // Storage path keyed on asset_id — asset is the canonical "what" record; photos travel with it.
      const fileName = `${userId}/appliances/${appliance.asset_id}_${Date.now()}.jpg`

      const { error: uploadError } = await supabase.storage
        .from('photos').upload(fileName, compressed, { upsert: true, contentType: 'image/jpeg' })
      if (uploadError) throw uploadError

      const { data } = supabase.storage.from('photos').getPublicUrl(fileName)
      const url = data.publicUrl

      const newPhotos = [...photos, url]

      await updateApplianceAsset(supabase, appliance.id, {
        photos: newPhotos,
        photo_uploaded_at: new Date().toISOString(),
      })

      recordProvenance({
        file: compressed,
        originalFile: file,
        storageBucket: 'photos',
        storagePath: fileName,
        resourceType: 'appliance',
        resourceId: appliance.asset_id,
        homeId: appliance.home_id || null,
        userId,
      }).catch(() => {})

      setPhotos(newPhotos)
      onPhotoUpdated ? onPhotoUpdated() : onUpdated()
    } catch (err) {
      setAlert({ type: 'error', msg: 'Error uploading photo. Please try again.' })
    }

    setPhotoUploading(false)
    e.target.value = ''
  }

  const handleRemovePhoto = async (urlToRemove) => {
    setPhotoUploading(true)
    const newPhotos = photos.filter(p => p !== urlToRemove)
    await updateApplianceAsset(supabase, appliance.id, { photos: newPhotos })
    setPhotos(newPhotos)
    setPhotoUploading(false)
    onPhotoUpdated ? onPhotoUpdated() : onUpdated()
  }

  const fetchApplianceParts = async () => {
    const { data } = await supabase
      .from('appliance_parts')
      .select(`
        id, interval_days, next_due_date, last_ordered_at, status, auto_order,
        custom_name, custom_asin, custom_price, notes,
        parts_catalog ( id, name, description, category, amazon_asin, unit_price )
      `)
      .eq('asset_id', appliance.asset_id)
      .eq('status', 'active')
      .order('created_at', { ascending: true })
    setApplianceParts(data || [])
    setPartsLoading(false)
  }

  const fetchPartsCatalog = async () => {
    const { data } = await supabase
      .from('parts_catalog')
      .select('id, name, description, category, amazon_asin, unit_price, notes')
      .order('category', { ascending: true })
      .order('name', { ascending: true })
    setPartsCatalog(data || [])
  }

  const triggerPartsScan = async (manualUrlToScan) => {
    setScanStatus('scanning')
    setScanDismissed(false)
    const userId = await getCurrentUserId()
    supabase.functions.invoke('scan-manual-parts', {
      body: {
        placement_id: appliance.id,
        manual_url: manualUrlToScan,
        user_id: userId,
      },
    }).catch(() => setScanStatus('failed'))
    let attempts = 0
    const scanPoll = setInterval(async () => {
      attempts++
      const { data } = await supabase
        .from('appliance_assets')
        .select('parts_scan_status, parts_scan_found')
        .eq('id', appliance.asset_id)
        .single()
      if (data?.parts_scan_status === 'done' || data?.parts_scan_status === 'failed') {
        setScanStatus(data.parts_scan_status)
        setScanFound(data.parts_scan_found || 0)
        clearInterval(scanPoll)
        if (data.parts_scan_status === 'done') fetchApplianceParts()
      } else if (attempts >= 12) {
        clearInterval(scanPoll)
      }
    }, 5000)
  }

  // ── MANUAL UPLOAD ─────────────────────────────────────────────────────────
  const handleManualUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 50 * 1024 * 1024) { setAlert({ type: 'error', msg: 'File must be under 50MB.' }); return }
    setManualUploading(true)
    setAlert(null)
    try {
      const userId = await getCurrentUserId()
      const fileName = `${userId}/manuals/${appliance.asset_id}.pdf`
      const { error: uploadError } = await supabase.storage.from('documents').upload(fileName, file, { upsert: true, contentType: file.type })
      if (uploadError) throw uploadError
      const { data } = supabase.storage.from('documents').getPublicUrl(fileName)
      await updateApplianceSlot(supabase, appliance.id, { manual_url: data.publicUrl })
      setManualUrl(data.publicUrl)
      onUpdated()
      triggerPartsScan(data.publicUrl)
    } catch {
      setAlert({ type: 'error', msg: 'Error uploading manual. Please try again.' })
    }
    setManualUploading(false)
    e.target.value = ''
  }

  // ── TASK COMPLETION ────────────────────────────────────────────────────────
  const handleTaskCheck = (taskKey, taskName, isCompleted, interval) => {
    if (isCompleted) {
      toggleTaskComplete(taskKey, null, null)
    } else {
      const today = new Date().toISOString().split('T')[0]
      setPendingTask({ taskKey, taskName, interval })
      setCompletionDate(today)
      setCostAmount(''); setCostProvider(''); setCostNote('')
    }
  }

  const toggleTaskComplete = async (taskKey, costEntry, lastDate) => {
    const isCurrentlyComplete = !!completedTasks[taskKey]
    const updated = { ...completedTasks }
    const updatedDates = { ...taskDates }

    if (isCurrentlyComplete) {
      // Unchecking — remove completion and dates
      delete updated[taskKey]
      delete updatedDates[taskKey]
    } else {
      // Checking — record completion with date
      const dateUsed = lastDate || new Date().toISOString().split('T')[0]
      updated[taskKey] = { date: dateUsed }
      const taskItem = schedule.find(t => `${t.task}_${t.interval}` === taskKey)
      const interval = pendingTask?.interval || taskItem?.interval || ''
      const nextDue = calcNextDue(dateUsed, interval)
      updatedDates[taskKey] = { lastCompleted: dateUsed, nextDue, interval }
    }

    setCompletedTasks(updated)
    setTaskDates(updatedDates)
    let updatedHistory = costHistory
    if (costEntry) { updatedHistory = [costEntry, ...costHistory]; setCostHistory(updatedHistory) }
    // Fields now live on three different tables — fan out the updates.
    await Promise.all([
      updateApplianceAsset(supabase, appliance.id, { completed_tasks: updated }),
      updateApplianceSlot(supabase, appliance.id, { task_dates: updatedDates }),
      updateAppliancePlacement(supabase, appliance.id, { cost_history: updatedHistory }),
    ])

    if (!isCurrentlyComplete) {
      const dateUsed = lastDate || new Date().toISOString().split('T')[0]
      const userId = await getCurrentUserId()
      await supabase.from('maintenance_logs').insert({
        user_id: userId,
        property_id: null,
        item_type: 'appliance',
        item_id: null,
        asset_id: appliance.asset_id,
        placement_id: appliance.id,
        item_name: appliance.nickname || appliance.name,
        task_name: taskKey,
        completed_date: dateUsed,
        cost: costEntry?.amount ?? null,
        provider: costEntry?.provider ?? null,
        note: costEntry?.note ?? null,
      })
    }

    onUpdated()
  }

  const handleSaveCost = async () => {
    if (!pendingTask) return
    const amount = costAmount ? parseFloat(costAmount) : null
    const dateUsed = completionDate || new Date().toISOString().split('T')[0]
    const costEntry = amount !== null ? { task: pendingTask.taskName, date: dateUsed, amount, provider: costProvider || null, note: costNote || null } : null
    await toggleTaskComplete(pendingTask.taskKey, costEntry, dateUsed)
    setPendingTask(null)
  }

  const handleGuideMarkComplete = async ({ taskKey, date, cost, provider, note }) => {
    const item = selectedTask?.task
    if (!item) return
    const dateUsed = date || new Date().toISOString().split('T')[0]
    const costEntry = cost != null ? { task: item.task, date: dateUsed, amount: cost, provider: provider || null, note: note || null } : null
    await toggleTaskComplete(taskKey, costEntry, dateUsed)
    setSelectedTask(null)
  }

  const handleGuideMarkIncomplete = () => {
    const item = selectedTask?.task
    if (!item) return
    const taskKey = `${item.task}_${item.interval}`
    toggleTaskComplete(taskKey, null, null)
    setSelectedTask(null)
  }

  const handleSkipCost = async () => {
    if (!pendingTask) return
    const dateUsed = completionDate || new Date().toISOString().split('T')[0]
    await toggleTaskComplete(pendingTask.taskKey, null, dateUsed)
    setPendingTask(null)
  }

  const handleAttachPart = async () => {
    if (!selectedCatalogPart || partSaving) return
    setPartSaving(true)
    const intervalDays = partIntervalDays ? parseInt(partIntervalDays) : null
    const nextDue = partNextDue || null
    const userId = await getCurrentUserId()
    // Look up property_id via slot → home → property so fetchDueParts (which still
    // filters by property_id) keeps working. See docs/known-tech-debt.md.
    let propertyId = null
    if (appliance.home_id) {
      const { data: home } = await supabase.from('homes').select('property_id').eq('id', appliance.home_id).single()
      propertyId = home?.property_id || null
    }
    const { error } = await supabase.from('appliance_parts').insert({
      user_id: userId,
      property_id: propertyId,
      asset_id: appliance.asset_id,
      part_id: selectedCatalogPart.id,
      interval_days: intervalDays,
      next_due_date: nextDue,
      status: 'active',
      auto_order: false,
    })
    if (!error) {
      await fetchApplianceParts()
      setShowAddPart(false)
      setSelectedCatalogPart(null)
      setPartIntervalDays('')
      setPartNextDue('')
    }
    setPartSaving(false)
  }

  const handleRemovePart = async (partId) => {
    await supabase.from('appliance_parts').update({ status: 'discontinued' }).eq('id', partId)
    await fetchApplianceParts()
  }

  // ── SHOP / REPLACEMENT SEARCH ─────────────────────────────────────────────
  // Unified query builder. Emits whichever spec dimensions are present so the
  // query stays useful even when specs are sparse. Falls back to type alone,
  // and ultimately to "appliance replacement" if even the type is missing.
  const buildReplacementQuery = (a) => {
    const specs = a?.specifications || {}
    const type = (a?.type || '').toLowerCase()
    const parts = []

    if (a?.energy_star === true) parts.push('Energy Star')

    if (specs.capacity_cu_ft)   parts.push(`${specs.capacity_cu_ft} cu ft`)
    if (specs.capacity_gallons) parts.push(`${specs.capacity_gallons} gallon`)
    if (specs.tonnage)          parts.push(`${specs.tonnage} ton`)
    if (specs.btu_per_hour)     parts.push(`${Number(specs.btu_per_hour).toLocaleString()} BTU`)
    if (specs.watts)            parts.push(`${specs.watts}W`)

    if (specs.form_factor)      parts.push(specs.form_factor)
    else if (specs.door_type)   parts.push(specs.door_type)

    // Fuel type prefix for types where it's part of the product category name
    const FUEL_RELEVANT = new Set(['dryer', 'oven', 'water_heater', 'hvac'])
    if (specs.fuel_type && FUEL_RELEVANT.has(type)) parts.push(specs.fuel_type)

    if (type) parts.push(type === 'hvac' ? 'HVAC' : type.replace(/_/g, ' '))

    if (parts.length === 0) return 'appliance replacement'
    return parts.join(' ').trim()
  }

  // Single source of truth for Amazon URLs — tag baked in, every caller gets it.
  const AFFILIATE_TAG = 'casavio-20'
  const amazonUrl = (query) =>
    `https://www.amazon.com/s?k=${encodeURIComponent((query || '').trim())}&tag=${AFFILIATE_TAG}`

  // DEV-only diagnostic for the Shop row — fires from the anchor onClick (does not
  // preventDefault, so the native navigation proceeds normally).
  const logShopClick = (label, query, url) => {
    if (DEV) {
      // eslint-disable-next-line no-console
      console.log(`%c[Shop] ${label}`, 'color:#B8721F;font-weight:bold',
        '\n  make:', appliance.make, '\n  model:', appliance.model,
        '\n  query:', query, '\n  url:', url)
    }
  }

  // Pre-computed Shop URLs and queries (rendered as anchor href values for iOS Safari trust).
  const shopMakeModel = `${appliance.make || ''} ${appliance.model || ''}`.trim()
  const shopHasMakeModel = !!(appliance.make && appliance.model)
  const partsQuery = `${shopMakeModel} replacement parts`
  const accessoriesQuery = `${shopMakeModel} accessories`
  const replacementQuery = buildReplacementQuery(appliance)
  const partsUrl = amazonUrl(partsQuery)
  const accessoriesUrl = amazonUrl(accessoriesQuery)
  const replacementUrl = amazonUrl(replacementQuery)

  // ── COST CALCULATIONS ──────────────────────────────────────────────────────
  const currentYear = new Date().getFullYear().toString()
  const thisYearHistory = costHistory.filter(h => h.date?.startsWith(currentYear))
  const totalSpent = thisYearHistory.reduce((s, h) => s + (h.amount || 0), 0)
  const totalSaved = thisYearHistory.reduce((s, h) => {
    if (!h.amount) return s
    const comp = getCostComparison(h.task, h.amount)
    return comp?.type === 'saved' ? s + (nationalAverages[h.task]?.avg - h.amount) : s
  }, 0)
  const liveCostComp = pendingTask && costAmount ? getCostComparison(pendingTask.taskName, parseFloat(costAmount)) : null
  const completedCount = Object.keys(completedTasks).length
  const totalTasks = schedule.length

  // ── SAVE / EDIT / REPLACE / DELETE ───────────────────────────────────────
  const saveNotes = async () => {
    setLoading(true)
    try {
      await updateApplianceAsset(supabase, appliance.id, { notes })
      setLoading(false)
      setNotesSaved(true); setTimeout(() => setNotesSaved(false), 2000); onUpdated()
    } catch {
      setLoading(false)
      setAlert({ type: 'error', msg: 'Error saving notes.' })
    }
  }

  const deleteAppliance = async () => {
    setLoading(true)
    try {
      await deleteApplianceRow(supabase, appliance.id)
      setLoading(false)
      onDeleted()
    } catch {
      setLoading(false)
      setAlert({ type: 'error', msg: 'Error deleting appliance.' })
    }
  }

  // Trigger background lookup and poll if URL is Google search or missing
  useEffect(() => {
    const needsLookup = !appliance.manual_url || appliance.manual_url.includes('google.com')
    if (!needsLookup || !appliance.make || !appliance.model) return
    setManualLookupStatus('loading')
    supabase.functions.invoke('lookup-manual', {
      body: { make: appliance.make, model: appliance.model, placement_id: appliance.id },
    })
    let attempts = 0
    pollRef.current = setInterval(async () => {
      attempts++
      const { data } = await supabase.from('appliance_slots').select('manual_url').eq('id', appliance.slot_id).single()
      if (data?.manual_url && !data.manual_url.includes('google.com')) {
        setManualUrl(data.manual_url)
        setManualLookupStatus('done')
        clearInterval(pollRef.current)
      } else if (attempts >= 3) {
        setManualLookupStatus('done')
        clearInterval(pollRef.current)
      }
    }, 10000)
    return () => clearInterval(pollRef.current)
  }, [appliance.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Fire Energy Star lookup only when status is unknown AND we haven't tried yet
  // (energy_star_confidence being set means we already ran a lookup for this appliance)
  useEffect(() => {
    if (appliance.energy_star === null && !appliance.energy_star_confidence && appliance.make && appliance.model) {
      supabase.functions.invoke('lookup-energy-star', {
        body: { make: appliance.make, model: appliance.model, asset_id: appliance.asset_id },
      }).catch(() => {})
    }
  }, [appliance.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (appliance.energy_star !== null) return
    setEnergyStarStatus('loading')
    const t = setTimeout(() => setEnergyStarStatus(s => s === 'loading' ? 'unavailable' : s), 5000)
    return () => clearTimeout(t)
  }, [appliance.id, energyStarAttempt]) // eslint-disable-line react-hooks/exhaustive-deps

  function retryEnergyStar() {
    supabase.functions.invoke('lookup-energy-star', {
      body: { make: appliance.make, model: appliance.model, asset_id: appliance.asset_id },
    }).catch(() => {})
    setEnergyStarAttempt(n => n + 1)
  }

  useEffect(() => {
    fetchApplianceParts()
    fetchPartsCatalog()
  }, [appliance.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const isRealManualUrl = manualUrl && !manualUrl.includes('google.com') && !manualUrl.includes('manualslib.com/search')
  const isDirectPdf = manualUrl?.endsWith('.pdf')

  return (
    <>
      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoChange} />
      <input ref={manualInputRef} type="file" accept=".pdf,application/pdf" style={{ display: 'none' }} onChange={handleManualUpload} />

      <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && !pendingTask && onClose()}>
        <div style={S.modal}>
          <div style={S.header}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
              <button
                onClick={onClose}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: '15px', padding: '4px 8px 4px 0', display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0, fontFamily: 'Georgia, serif' }}
              >
                ← Back
              </button>
              <div style={{ ...S.title, fontSize: '18px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {mode === 'view'
                  ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}><AppIcon size={18} strokeWidth={1.5} color={C.brown} />{appliance.name}</span>
                  : 'Remove Appliance'}
              </div>
            </div>
          </div>

          <div style={S.body}>
            {alert && <div style={S.alert(alert.type)}>{alert.msg}</div>}

            {/* ── VIEW MODE ── */}
            {mode === 'view' && (
              <div>
                {/* ── PHOTO GALLERY ── */}
                {/* Hero: user photo (photos[0]) or stock/ManualsLib thumbnail */}
                <div style={S.photoBox}>
                  {coverPhoto && !imgFailed ? (
                    <img src={coverPhoto} alt={`${appliance.make} ${appliance.model}`} style={S.photoImg} onError={() => setImgFailed(true)} />
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                      <AppIcon size={48} strokeWidth={1} color={C.amber} />
                      <div style={{ fontSize: '13px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>{appliance.make} {appliance.model}</div>
                    </div>
                  )}
                </div>

                {/* User photo gallery */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <div style={S.sectionTitle}>MY PHOTOS ({photos.length}/{MAX_PHOTOS})</div>
                    {photos.length < MAX_PHOTOS && (
                      <button style={S.photoBtn} onClick={handlePhotoClick}>
                        {photoUploading ? 'Uploading...' : '+ Add Photo'}
                      </button>
                    )}
                  </div>

                  {photos.length > 0 ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                      {photos.map((url, i) => (
                        <div key={i} style={{ position: 'relative', borderRadius: '10px', overflow: 'hidden', aspectRatio: '1', background: C.bg }}>
                          <img
                            src={url} alt={`Photo ${i + 1}`}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          />
                          <button
                            onClick={() => handleRemovePhoto(url)}
                            style={{
                              position: 'absolute', top: '4px', right: '4px',
                              background: 'rgba(0,0,0,0.6)', border: 'none',
                              borderRadius: '50%', width: '22px', height: '22px',
                              color: '#fff', fontSize: '12px', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              lineHeight: 1,
                            }}
                          >×</button>
                        </div>
                      ))}
                      {/* Add more slot */}
                      {photos.length < MAX_PHOTOS && (
                        <div
                          onClick={handlePhotoClick}
                          style={{
                            borderRadius: '10px', border: `2px dashed ${C.border}`,
                            aspectRatio: '1', display: 'flex', flexDirection: 'column',
                            alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', gap: '4px',
                          }}
                        >
                          <div style={{ fontSize: '20px', color: C.muted }}>+</div>
                          <div style={{ fontSize: '10px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>Add</div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      onClick={handlePhotoClick}
                      style={{
                        border: `2px dashed ${C.border}`, borderRadius: '12px',
                        padding: '24px', textAlign: 'center', cursor: 'pointer',
                      }}
                    >
                      <Camera size={28} strokeWidth={1.5} color={C.muted} style={{ marginBottom: '6px' }} />
                      <div style={{ fontSize: '13px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>
                        Add your own photos — installation, label, damage, etc.
                      </div>
                      <div style={{ fontSize: '11px', color: C.amber, fontFamily: 'Calibri, sans-serif', marginTop: '4px' }}>
                        Up to {MAX_PHOTOS} photos
                      </div>
                    </div>
                  )}
                </div>

                {/* Status */}
                <div style={{ marginBottom: '16px' }}>
                  <span style={S.statusBadge(appliance.status)}>
                    {appliance.status === 'good' ? '✓ Up to date' : appliance.status === 'due' ? '⚠ Service due' : '✗ Overdue'}
                  </span>
                </div>

                {appliance.data_plate_unavailable && (
                  <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '10px 14px', fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginBottom: '16px' }}>
                    No data plate captured — fill in details manually.
                  </div>
                )}

                {/* Details — every cell is inline-editable. Tap to edit, blur/Enter to save. */}
                <div style={S.sectionTitle}>Appliance Details</div>
                <div style={S.detailGrid}>
                  <InlineEditableField label="Manufacturer"    value={appliance.make}         placementId={appliance.id} fieldName="make"         fieldOwner="asset"     onSaved={onUpdated} placeholder="Tap to add" />
                  <InlineEditableField label="Model"           value={appliance.model}        placementId={appliance.id} fieldName="model"        fieldOwner="asset"     onSaved={onUpdated} placeholder="Tap to add" />
                  <InlineEditableField label="Serial Number"   value={appliance.serial}       placementId={appliance.id} fieldName="serial"       fieldOwner="asset"     onSaved={onUpdated} placeholder="Tap to add" />
                  <InlineEditableField label="Manufactured"    value={appliance.manufactured} placementId={appliance.id} fieldName="manufactured" fieldOwner="asset"     onSaved={onUpdated} placeholder="Tap to add" />
                </div>
                {(() => {
                  const specs = appliance.specifications || {}
                  const series = specs.series || null
                  const platform = specs.platform || null
                  const features = (Array.isArray(specs.features) ? specs.features : []).filter(Boolean)
                  if (!series && !platform && features.length === 0) return null
                  return (
                    <div style={S.identityBadgeRow}>
                      {series && <span style={S.identityBadge} title="Series">{series}</span>}
                      {platform && <span style={S.identityBadge} title="Platform">{platform}</span>}
                      {features.map((f, i) => (
                        <span key={`feat-${i}-${f}`} style={S.identityBadge} title={`Feature: ${f}`}>{f}</span>
                      ))}
                    </div>
                  )
                })()}
                <div style={S.detailGrid}>
                  <InlineEditableField label="Location"        value={appliance.location}     placementId={appliance.id} fieldName="location"     fieldOwner="slot"      onSaved={onUpdated} placeholder="Tap to add" />
                  <InlineEditableField label="Nickname"        value={appliance.nickname}     placementId={appliance.id} fieldName="nickname"     fieldOwner="slot"      onSaved={onUpdated} placeholder="Tap to add" />
                </div>
                {/* Documented (auto, readonly) + Installed (user-set, editable, defaults to null) */}
                <div style={S.detailGrid}>
                  <div style={S.detailItem}>
                    <div style={S.detailLabel}>Documented</div>
                    <div style={S.detailValue}>{appliance.documented_date ? formatDate(appliance.documented_date) : '—'}</div>
                  </div>
                  <InlineEditableField label="Installed"     value={appliance.installed_date} placementId={appliance.id} fieldName="installed_date" fieldOwner="placement" inputType="date" onSaved={onUpdated} placeholder="Not set" />
                </div>

                {appliance.make && appliance.model && (
                  <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                    {appliance.energy_star === true && (
                      <span style={{ fontSize: '11px', color: C.success, background: '#EBF5EF', border: `1px solid ${C.success}44`, borderRadius: '20px', padding: '4px 12px', fontFamily: 'Calibri, sans-serif', fontWeight: '700' }}>
                        ⚡ ENERGY STAR{appliance.energy_star_tier ? ` — ${appliance.energy_star_tier}` : ' Certified'}
                      </span>
                    )}
                    {appliance.energy_star === false && appliance.energy_star_confidence === 'high' && (
                      <span style={{ fontSize: '11px', color: C.muted, background: C.bg, border: `1px solid ${C.border}`, borderRadius: '20px', padding: '4px 12px', fontFamily: 'Calibri, sans-serif' }}>
                        Not ENERGY STAR Certified
                      </span>
                    )}
                    {appliance.energy_star === null && (
                      energyStarStatus === 'unavailable'
                        ? <span style={{ fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            Energy Star status unavailable
                            <button onClick={retryEnergyStar} title="Retry" style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, padding: '0 2px', lineHeight: 1 }}>
                              <RefreshCw size={11} strokeWidth={2} />
                            </button>
                          </span>
                        : <span style={{ fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif', fontStyle: 'italic' }}>
                            Checking Energy Star status…
                          </span>
                    )}
                    {appliance.est_annual_energy_cost && (
                      <span style={{ fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>
                        ~${appliance.est_annual_energy_cost}/yr est. energy cost
                      </span>
                    )}
                  </div>
                )}

                <div style={S.divider} />

                {/* Owner's Manual */}
                <div style={S.sectionTitle}>Owner's Manual</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
                  {isRealManualUrl && !showReplaceField ? (
                    <>
                      <a href={manualUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                        <div style={{ background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`, borderRadius: '10px', padding: '12px 16px', fontSize: '14px', color: C.card, fontWeight: '700', fontFamily: 'Georgia, serif', textAlign: 'center', cursor: 'pointer' }}>
                          {isDirectPdf ? '📄 Open Manual PDF' : '📖 Open Manual'}
                        </div>
                      </a>
                      <div
                        style={{ textAlign: 'center', fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif', cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => { setShowReplaceField(true); setPastedUrl('') }}
                      >
                        Wrong manual? Replace
                      </div>
                    </>
                  ) : manualLookupStatus === 'loading' ? (
                    <>
                      <div style={{ fontSize: '13px', color: C.muted, fontFamily: 'Calibri, sans-serif', padding: '6px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ display: 'inline-block', width: '12px', height: '12px', border: `2px solid ${C.border}`, borderTopColor: C.amber, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                        🔍 Still finding your manual...
                      </div>
                      {appliance.make && appliance.model && (
                        <a href={getManualUrl(appliance.make, appliance.model)} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                          <div style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif', cursor: 'pointer' }}>
                            Search manually →
                          </div>
                        </a>
                      )}
                    </>
                  ) : (
                    <>
                      {appliance.make && appliance.model && (
                        <a href={getManualUrl(appliance.make, appliance.model)} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                          <div style={{ background: C.bg, border: `1px solid ${C.amber}44`, borderRadius: '10px', padding: '11px 16px', fontSize: '14px', color: C.amber, fontFamily: 'Calibri, sans-serif', textAlign: 'center', cursor: 'pointer', fontWeight: '600' }}>
                            🔍 Find Manual
                          </div>
                        </a>
                      )}
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <input
                          style={{ flex: 1, background: C.bg, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 12px', fontSize: '13px', fontFamily: 'Calibri, sans-serif', color: C.brown, outline: 'none', boxSizing: 'border-box' }}
                          placeholder="Paste manual URL to save it"
                          value={pastedUrl}
                          onChange={e => setPastedUrl(e.target.value)}
                        />
                        <button
                          disabled={!pastedUrl.trim()}
                          onClick={async () => {
                            const url = pastedUrl.trim()
                            if (!url) return
                            await updateApplianceSlot(supabase, appliance.id, { manual_url: url })
                            setManualUrl(url)
                            setPastedUrl('')
                            setShowReplaceField(false)
                            onUpdated()
                            if (url.endsWith('.pdf') || url.includes('/pdf') || url.includes('manualslib') || url.includes('manua.ls')) {
                              triggerPartsScan(url)
                            }
                          }}
                          style={{ background: pastedUrl.trim() ? C.amber : C.border, border: 'none', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: C.card, cursor: pastedUrl.trim() ? 'pointer' : 'default', fontFamily: 'Calibri, sans-serif', fontWeight: '600', whiteSpace: 'nowrap', flexShrink: 0 }}
                        >
                          Save
                        </button>
                      </div>
                      {showReplaceField && appliance.make && appliance.model && (
                        <button
                          onClick={() => {
                            setManualLookupStatus('loading')
                            setShowReplaceField(false)
                            clearInterval(pollRef.current)
                            supabase.functions.invoke('lookup-manual', {
                              body: { make: appliance.make, model: appliance.model, placement_id: appliance.id },
                            })
                            let attempts = 0
                            pollRef.current = setInterval(async () => {
                              attempts++
                              const { data } = await supabase.from('appliance_slots').select('manual_url').eq('id', appliance.slot_id).single()
                              if (data?.manual_url && !data.manual_url.includes('google.com')) {
                                setManualUrl(data.manual_url)
                                setManualLookupStatus('done')
                                clearInterval(pollRef.current)
                              } else if (attempts >= 3) {
                                setManualLookupStatus('done')
                                clearInterval(pollRef.current)
                              }
                            }, 10000)
                          }}
                          style={{ background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '8px', padding: '9px 14px', fontSize: '12px', color: C.muted, cursor: 'pointer', fontFamily: 'Calibri, sans-serif' }}
                        >
                          ↺ Re-search
                        </button>
                      )}
                    </>
                  )}
                  <button
                    onClick={() => manualInputRef.current?.click()}
                    disabled={manualUploading}
                    style={{ background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '10px', padding: '11px 16px', fontSize: '13px', color: manualUploading ? C.muted : C.amber, cursor: manualUploading ? 'default' : 'pointer', fontFamily: 'Calibri, sans-serif', textAlign: 'center' }}
                  >
                    {manualUploading ? 'Uploading...' : isRealManualUrl ? '↑ Replace Manual PDF' : '↑ Upload Manual PDF'}
                  </button>
                </div>

                {/* Parts scan status banner */}
                {scanStatus === 'scanning' && (
                  <div style={{ marginTop: '12px', background: '#FBF3E8', border: `1px solid ${C.amber}44`, borderRadius: '10px', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px', color: C.amber, fontFamily: 'Calibri, sans-serif' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite', flexShrink: 0 }}>
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                    <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                    Scanning manual for parts & supplies…
                  </div>
                )}
                {scanStatus === 'done' && !scanDismissed && scanFound > 0 && (
                  <div style={{ marginTop: '12px', background: C.successBg, border: `1px solid ${C.success}44`, borderRadius: '10px', padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12px', color: C.success, fontFamily: 'Calibri, sans-serif' }}>
                    <span>✓ Found {scanFound} part{scanFound !== 1 ? 's' : ''} from your manual</span>
                    <button onClick={() => setScanDismissed(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.success, fontSize: '16px', lineHeight: 1, padding: '0 0 0 8px' }}>×</button>
                  </div>
                )}
                {scanStatus === 'done' && !scanDismissed && scanFound === 0 && (
                  <div style={{ marginTop: '12px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>
                    <span>No replaceable parts found in this manual</span>
                    <button onClick={() => setScanDismissed(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: '16px', lineHeight: 1, padding: '0 0 0 8px' }}>×</button>
                  </div>
                )}

                <div style={S.divider} />

                {/* Parts & Supplies */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div style={S.sectionTitle}>Parts & Supplies</div>
                  <button
                    onClick={() => { setShowAddPart(true); setSelectedCatalogPart(null); setPartIntervalDays(''); setPartNextDue('') }}
                    style={{ background: 'transparent', border: `1px solid ${C.amber}`, borderRadius: '8px', padding: '5px 12px', fontSize: '11px', color: C.amber, cursor: 'pointer', fontFamily: 'Calibri, sans-serif', display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    <Plus size={12} strokeWidth={2} /> Add Part
                  </button>
                </div>

                {partsLoading ? (
                  <div style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginBottom: '16px' }}>Loading...</div>
                ) : applianceParts.length === 0 ? (
                  <div
                    onClick={() => { setShowAddPart(true); setSelectedCatalogPart(null); setPartIntervalDays(''); setPartNextDue('') }}
                    style={{ border: `2px dashed ${C.border}`, borderRadius: '10px', padding: '18px', textAlign: 'center', cursor: 'pointer', marginBottom: '4px' }}
                  >
                    <ShoppingBag size={24} strokeWidth={1.5} color={C.muted} style={{ marginBottom: '6px' }} />
                    <div style={{ fontSize: '13px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>No parts attached yet</div>
                    <div style={{ fontSize: '11px', color: C.amber, fontFamily: 'Calibri, sans-serif', marginTop: '4px' }}>Tap to add a part or supply →</div>
                  </div>
                ) : (
                  <div style={{ background: C.bg, borderRadius: '10px', border: `1px solid ${C.border}`, overflow: 'hidden', marginBottom: '4px' }}>
                    {applianceParts.map((part, i) => {
                      const catalog = part.parts_catalog
                      const name = part.custom_name || catalog?.name || 'Part'
                      const price = part.custom_price ?? catalog?.unit_price

                      const today = new Date(); today.setHours(0, 0, 0, 0)
                      const dueDate = part.next_due_date ? new Date(part.next_due_date) : null
                      const diffDays = dueDate ? Math.ceil((dueDate - today) / 86400000) : null
                      const isOverdue = diffDays !== null && diffDays < 0
                      const badgeColor = isOverdue ? C.danger : diffDays !== null && diffDays <= 30 ? C.amber : C.success
                      const badgeBg = isOverdue ? '#FDECEA' : diffDays !== null && diffDays <= 30 ? '#FBF3E8' : '#EBF5EF'
                      const badgeLabel = diffDays === null ? 'No schedule' : isOverdue ? 'Overdue' : diffDays === 0 ? 'Due today' : `Due in ${diffDays}d`

                      return (
                        <div key={part.id} style={{
                          padding: '12px 14px',
                          borderBottom: i < applianceParts.length - 1 ? `1px solid ${C.border}` : 'none',
                          display: 'flex', alignItems: 'center', gap: '10px',
                        }}>
                          <ShoppingBag size={16} strokeWidth={1.5} color={C.amber} style={{ flexShrink: 0 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: C.brown }}>{name}</div>
                            {price != null && (
                              <div style={{ fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>
                                ${parseFloat(price).toFixed(2)} · {part.interval_days ? `every ${part.interval_days}d` : 'no interval set'}
                              </div>
                            )}
                          </div>
                          <div style={{
                            fontSize: '10px', padding: '2px 7px', borderRadius: '20px',
                            background: badgeBg, color: badgeColor,
                            fontFamily: 'Calibri, sans-serif',
                            border: `1px solid ${badgeColor}44`,
                            flexShrink: 0, whiteSpace: 'nowrap',
                          }}>
                            {badgeLabel}
                          </div>
                          <button
                            onClick={() => handleRemovePart(part.id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', flexShrink: 0, opacity: 0.4 }}
                            title="Remove part"
                          >
                            <Trash2 size={14} strokeWidth={1.5} color={C.danger} />
                          </button>
                        </div>
                      )
                    })}
                  </div>
                )}

                <div style={S.divider} />

                {/* Maintenance Schedule */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <div style={S.sectionTitle}>Maintenance Schedule</div>
                  {completedCount > 0 && <div style={{ fontSize: '11px', color: C.success, fontFamily: 'Calibri, sans-serif', fontWeight: '600' }}>{completedCount}/{totalTasks} completed ✓</div>}
                </div>
                <div style={{ fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginBottom: '10px' }}>
                  Tap a task for the how-to guide · Check the circle to mark complete
                </div>
                <div style={{ marginBottom: '20px' }}>
                  {schedule.map((item, i) => {
                    const taskKey = `${item.task}_${item.interval}`
                    const isCompleted = !!completedTasks[taskKey]
                    const td = taskDates[item.task]
                    return (
                      <MaintenanceTaskTile
                        key={i}
                        task={item}
                        nextDue={td?.nextDue || null}
                        status={getDueStatus(td?.nextDue)}
                        isCompleted={isCompleted}
                        lastCompleted={td?.lastCompleted || null}
                        applianceName={displayName}
                        showApplianceName={false}
                        selfManaged={isSelfManaged(item.interval)}
                        onCheck={() => handleTaskCheck(taskKey, item.task, isCompleted, item.interval)}
                        onOpen={() => !isSelfManaged(item.interval) && setSelectedTask({ task: item, applianceName: displayName, applianceType: appliance.type })}
                        onOrderNow={getOrderNow(item)}
                        isLast={i === schedule.length - 1}
                      />
                    )
                  })}
                </div>

                <div style={S.divider} />

                {/* Cost History */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div style={S.sectionTitle}>Cost History</div>
                  {thisYearHistory.length > 0 && <div style={{ fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>{currentYear}</div>}
                </div>

                {costHistory.length === 0 ? (
                  <div style={{ fontSize: '13px', color: C.muted, fontStyle: 'italic', fontFamily: 'Calibri, sans-serif', marginBottom: '16px' }}>
                    No costs logged yet. Mark tasks complete to start tracking.
                  </div>
                ) : (
                  <>
                    {thisYearHistory.length > 0 && (
                      <div style={S.costSummaryCard}>
                        <div style={S.costSummaryItem}><div style={S.costSummaryValue}>${totalSpent.toFixed(0)}</div><div style={S.costSummaryLabel}>Spent {currentYear}</div></div>
                        <div style={S.costSummaryItem}><div style={{ ...S.costSummaryValue, color: C.success }}>${totalSaved.toFixed(0)}</div><div style={S.costSummaryLabel}>Saved vs avg</div></div>
                        <div style={S.costSummaryItem}><div style={S.costSummaryValue}>{thisYearHistory.length}</div><div style={S.costSummaryLabel}>Jobs logged</div></div>
                      </div>
                    )}
                    {costHistory.map((entry, i) => {
                      const comp = entry.amount ? getCostComparison(entry.task, entry.amount) : null
                      const nat = nationalAverages[entry.task]
                      return (
                        <div key={i} style={S.costHistoryItem}>
                          <div style={{ flex: 1 }}>
                            <div style={S.costHistoryTask}>{entry.task}</div>
                            <div style={S.costHistoryMeta}>{formatDate(entry.date)}{entry.provider ? ` · ${entry.provider}` : ''}{entry.note ? ` · ${entry.note}` : ''}</div>
                            {nat?.avg > 0 && <div style={{ fontSize: '10px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginTop: '2px' }}>National avg: ${nat.avg}</div>}
                          </div>
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            {entry.amount
                              ? <><div style={{ fontSize: '15px', fontWeight: '700', color: C.brown }}>${entry.amount.toFixed(0)}</div>{comp && <div style={S.costHistoryComparison(comp.type)}>{comp.type === 'saved' ? '💚 ' : comp.type === 'over' ? '⚠ ' : '→ '}{comp.text}</div>}</>
                              : <div style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif', fontStyle: 'italic' }}>DIY / no cost</div>
                            }
                          </div>
                        </div>
                      )
                    })}
                  </>
                )}

                <div style={S.divider} />

                {/* Notes */}
                <div style={S.sectionTitle}>Notes about this appliance</div>
                <textarea style={S.notesBox} placeholder="e.g. Unit is in the attic, accessed through hall closet. Bring a ladder." value={notes} onChange={e => { setNotes(e.target.value); setNotesSaved(false) }} />
                <button style={S.saveNotesBtn} onClick={saveNotes} disabled={loading}>{notesSaved ? '✓ Saved' : loading ? 'Saving...' : 'Save Notes'}</button>

                {/* Completed Tasks */}
                {(() => {
                  const completedTasksList = schedule
                    .filter(item => {
                      const taskKey = `${item.task}_${item.interval}`
                      return !!completedTasks[taskKey]
                    })
                    .map(item => {
                      const taskKey = `${item.task}_${item.interval}`
                      const date = completedTasks[taskKey]?.date
                      const costEntry = costHistory.find(h => h.task === item.task && h.date === date)
                      return { task: item.task, date, cost: costEntry?.amount ?? null, provider: costEntry?.provider || null }
                    })
                    .sort((a, b) => new Date(b.date) - new Date(a.date))

                  if (completedTasksList.length === 0) return null
                  return (
                    <div>
                      <div style={S.divider} />
                      <div
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', cursor: 'pointer' }}
                        onClick={() => setShowCompletedTasks(!showCompletedTasks)}
                      >
                        <div style={S.sectionTitle}>Completed Tasks ({completedTasksList.length})</div>
                        <div style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif', flexShrink: 0 }}>{showCompletedTasks ? '▲ Hide' : '▼ Show'}</div>
                      </div>
                      {showCompletedTasks && (
                        <div style={{ marginBottom: '8px' }}>
                          {completedTasksList.map((t, i) => (
                            <div key={i} style={{ padding: '10px 0', borderBottom: i < completedTasksList.length - 1 ? `1px solid ${C.border}` : 'none', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: '13px', fontWeight: '600', color: C.success }}>✓ {t.task}</div>
                                <div style={{ fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginTop: '2px' }}>
                                  {formatDate(t.date)}{t.provider ? ` · ${t.provider}` : ''}
                                </div>
                              </div>
                              {t.cost != null && (
                                <div style={{ fontSize: '14px', fontWeight: '700', color: C.brown, flexShrink: 0 }}>${parseFloat(t.cost).toFixed(0)}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })()}

                {/* ── SHOP ── */}
                {/* Anchor tags (not buttons) so iOS Safari treats taps as trusted
                    navigation, not popup-blocker-prone window.open() calls. */}
                <div style={S.divider} />
                <div style={S.sectionTitle}>Shop</div>
                <style>{`
                  .casavio-shop-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
                  @media (max-width: 480px) { .casavio-shop-row { grid-template-columns: 1fr; } }
                  .casavio-shop-btn {
                    background: ${C.card}; color: ${C.brown};
                    border: 1px solid ${C.border}; border-radius: 8px;
                    padding: 10px 12px; font-size: 13px;
                    font-family: Calibri, sans-serif; cursor: pointer;
                    text-decoration: none;
                    transition: border-color 0.15s, background 0.15s;
                    display: flex; align-items: center; justify-content: center; gap: 6px;
                  }
                  .casavio-shop-btn:hover { border-color: ${C.amber}; }
                  .casavio-shop-btn--disabled {
                    opacity: 0.5; pointer-events: none; cursor: default;
                  }
                `}</style>
                <div className="casavio-shop-row">
                  <a
                    href={partsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`casavio-shop-btn${shopHasMakeModel ? '' : ' casavio-shop-btn--disabled'}`}
                    aria-disabled={!shopHasMakeModel}
                    onClick={() => logShopClick('Find Parts', partsQuery, partsUrl)}
                  >
                    <ShoppingCart size={14} strokeWidth={2} /> Find Parts
                  </a>
                  <a
                    href={accessoriesUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`casavio-shop-btn${shopHasMakeModel ? '' : ' casavio-shop-btn--disabled'}`}
                    aria-disabled={!shopHasMakeModel}
                    onClick={() => logShopClick('Find Accessories', accessoriesQuery, accessoriesUrl)}
                  >
                    <ShoppingCart size={14} strokeWidth={2} /> Find Accessories
                  </a>
                  <a
                    href={replacementUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="casavio-shop-btn"
                    onClick={() => logShopClick('Find a Replacement', replacementQuery, replacementUrl)}
                  >
                    <ShoppingCart size={14} strokeWidth={2} /> Find a Replacement
                  </a>
                </div>
                <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif', fontStyle: 'italic' }}>
                  Amazon affiliate links — Casavio earns from qualifying purchases
                </div>

                {/* ── DONE (closes the modal; no save side-effect) ── */}
                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    width: '100%', marginTop: '24px',
                    background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
                    border: 'none', borderRadius: '8px',
                    padding: '12px 16px', fontSize: '14px', fontWeight: '700',
                    color: '#fff', cursor: 'pointer',
                    fontFamily: 'Georgia, serif', letterSpacing: '1px', textTransform: 'uppercase',
                  }}
                >
                  Done
                </button>

                {/* ── REMOVE (demoted to small danger text link with breathing room) ── */}
                <div style={{ marginTop: '32px', textAlign: 'center' }}>
                  <span
                    onClick={() => setMode('confirmDelete')}
                    style={{ fontSize: '12px', color: C.danger, fontFamily: 'Calibri, sans-serif', cursor: 'pointer' }}
                  >
                    Remove appliance
                  </span>
                </div>
              </div>
            )}

            {/* ── CONFIRM DELETE (soft delete — ends placement, asset+slot history preserved) ── */}
            {mode === 'confirmDelete' && (
              <div>
                <div style={{ ...S.sectionTitle, fontSize: '16px', color: C.brown, letterSpacing: '0.5px', textTransform: 'none', marginBottom: '12px', fontFamily: 'Georgia, serif', fontWeight: '700' }}>
                  Remove this appliance from your home?
                </div>
                <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '14px 16px', marginBottom: '20px', fontSize: '13px', color: C.brown, fontFamily: 'Calibri, sans-serif', lineHeight: 1.6 }}>
                  This appliance will no longer appear in your dashboard. We'll keep its history (photos, service records, manuals) in case you ever need to reference it for warranty claims, insurance, or selling your home.
                </div>
                <div style={S.btnRow}>
                  <button style={S.editBtn} onClick={() => setMode('view')}>Cancel</button>
                  <button style={{ ...S.replaceBtn, background: C.danger }} onClick={deleteAppliance} disabled={loading}>{loading ? 'Removing...' : 'Remove appliance'}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedTask && (
        <MaintenanceTaskModal
          task={selectedTask.task}
          applianceName={selectedTask.applianceName}
          applianceType={selectedTask.applianceType}
          completedTasks={completedTasks}
          taskDates={taskDates}
          onComplete={handleGuideMarkComplete}
          onMarkIncomplete={handleGuideMarkIncomplete}
          onDismiss={() => setSelectedTask(null)}
          onOrderNow={getOrderNow(selectedTask.task)}
        />
      )}

      {/* ── ADD PART MODAL ── */}
      {showAddPart && (
        <div style={S.costModalOverlay} onClick={e => e.target === e.currentTarget && setShowAddPart(false)}>
          <div style={S.costModal}>
            <div style={{ fontSize: '18px', fontWeight: '700', color: C.brown, marginBottom: '4px' }}>Add a Part or Supply</div>
            <div style={{ fontSize: '13px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginBottom: '20px' }}>
              Choose from the catalog to track and order this item
            </div>

            <label style={S.costLabel}>Select Part</label>
            <div style={{ position: 'relative', marginBottom: '16px' }}>
              <select
                value={selectedCatalogPart?.id || ''}
                onChange={e => {
                  const part = partsCatalog.find(p => p.id === e.target.value)
                  setSelectedCatalogPart(part || null)
                  if (part) {
                    const defaults = {
                      hvac_filter: '90', refrigerator: '180', water_heater: '365',
                      dishwasher: '180', washer: '30', dryer: '365',
                      humidifier: '365', disposal: '30',
                    }
                    const interval = defaults[part.category] || ''
                    setPartIntervalDays(interval)
                    if (interval) {
                      const d = new Date()
                      d.setDate(d.getDate() + parseInt(interval))
                      setPartNextDue(d.toISOString().split('T')[0])
                    }
                  }
                }}
                style={{
                  width: '100%', background: C.bg, border: `1px solid ${C.border}`,
                  borderRadius: '8px', padding: '11px 36px 11px 14px',
                  fontSize: '14px', fontFamily: 'Calibri, sans-serif',
                  color: selectedCatalogPart ? C.brown : C.muted,
                  outline: 'none', appearance: 'none', boxSizing: 'border-box',
                  cursor: 'pointer',
                }}
              >
                <option value="">Choose from catalog...</option>
                {(() => {
                  const grouped = partsCatalog.reduce((acc, p) => {
                    const cat = p.category || 'other'
                    if (!acc[cat]) acc[cat] = []
                    acc[cat].push(p)
                    return acc
                  }, {})
                  return Object.entries(grouped).map(([cat, parts]) => (
                    <optgroup key={cat} label={cat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}>
                      {parts.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.name}{p.unit_price ? ` — $${parseFloat(p.unit_price).toFixed(2)}` : ''}
                        </option>
                      ))}
                    </optgroup>
                  ))
                })()}
              </select>
              <ChevronDown size={16} strokeWidth={1.5} color={C.muted} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
            </div>

            {selectedCatalogPart && (
              <div style={{ background: C.bg, borderRadius: '8px', border: `1px solid ${C.border}`, padding: '10px 14px', marginBottom: '16px', fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif', lineHeight: 1.6 }}>
                {selectedCatalogPart.description && <div>{selectedCatalogPart.description}</div>}
                {selectedCatalogPart.notes && <div style={{ marginTop: '2px', color: C.amber }}>{selectedCatalogPart.notes}</div>}
              </div>
            )}

            <label style={S.costLabel}>Replace Every (days)</label>
            <input
              type="number"
              placeholder="e.g. 90 for quarterly, 180 for 6 months"
              value={partIntervalDays}
              onChange={e => {
                setPartIntervalDays(e.target.value)
                if (e.target.value) {
                  const d = new Date()
                  d.setDate(d.getDate() + parseInt(e.target.value))
                  setPartNextDue(d.toISOString().split('T')[0])
                }
              }}
              style={{ ...S.costInput, width: '100%', boxSizing: 'border-box', marginBottom: '16px' }}
            />

            <label style={S.costLabel}>Next Due Date</label>
            <input
              type="date"
              value={partNextDue}
              onChange={e => setPartNextDue(e.target.value)}
              style={{ ...S.costInput, width: '100%', boxSizing: 'border-box', marginBottom: '20px' }}
            />

            <button
              disabled={!selectedCatalogPart || partSaving}
              onClick={handleAttachPart}
              style={{
                ...S.costSaveBtn,
                opacity: !selectedCatalogPart || partSaving ? 0.5 : 1,
                cursor: !selectedCatalogPart || partSaving ? 'default' : 'pointer',
              }}
            >
              {partSaving ? 'Saving...' : 'Attach Part'}
            </button>
            <button style={S.costSkipBtn} onClick={() => setShowAddPart(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── COST LOG MODAL ── */}
      {pendingTask && (
        <div style={S.costModalOverlay}>
          <div style={S.costModal}>
            <div style={{ fontSize: '18px', fontWeight: '700', color: C.brown, marginBottom: '4px' }}>✓ {pendingTask.taskName}</div>
            <div style={{ fontSize: '13px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginBottom: '20px' }}>{displayName} — when did you last do this?</div>

            {/* Date picker */}
            <label style={S.costLabel}>Completion Date</label>
            <input
              type="date"
              style={{ ...S.costInput, width: '100%', boxSizing: 'border-box', marginBottom: '6px' }}
              value={completionDate}
              max={new Date().toISOString().split('T')[0]}
              onChange={e => setCompletionDate(e.target.value)}
            />
            {completionDate && pendingTask.interval && calcNextDue(completionDate, pendingTask.interval) && (
              <div style={{ fontSize: '11px', color: C.amber, fontFamily: 'Calibri, sans-serif', marginBottom: '16px' }}>
                Next due: {formatDate(calcNextDue(completionDate, pendingTask.interval))}
              </div>
            )}
            {!(completionDate && pendingTask.interval && calcNextDue(completionDate, pendingTask.interval)) && (
              <div style={{ marginBottom: '16px' }} />
            )}

            {nationalAverages[pendingTask.taskName]?.avg > 0 && (
              <div style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginBottom: '14px' }}>
                National average for this task: <strong style={{ color: C.brown }}>${nationalAverages[pendingTask.taskName].avg}</strong>
              </div>
            )}
            <label style={S.costLabel}>Amount Paid (optional)</label>
            <div style={{ position: 'relative', marginBottom: '12px' }}>
              <span style={{ position: 'absolute', left: '14px', top: '11px', fontSize: '15px', color: C.muted, fontFamily: 'Georgia, serif' }}>$</span>
              <input style={{ ...S.costInput, paddingLeft: '28px', width: '100%', boxSizing: 'border-box' }} type="number" placeholder="0.00" value={costAmount} onChange={e => setCostAmount(e.target.value)} autoFocus />
            </div>
            {liveCostComp && <div style={S.natAvgBadge(liveCostComp.type)}>{liveCostComp.type === 'saved' ? '💚 ' : liveCostComp.type === 'over' ? '⚠ ' : '✓ '}{liveCostComp.text}</div>}
            <label style={S.costLabel}>Who Did It? (optional)</label>
            <input style={{ ...S.costInput, width: '100%', boxSizing: 'border-box', marginBottom: '12px' }} placeholder="e.g. ABC HVAC, Self / DIY" value={costProvider} onChange={e => setCostProvider(e.target.value)} />
            <label style={S.costLabel}>Note (optional)</label>
            <input style={{ ...S.costInput, width: '100%', boxSizing: 'border-box', marginBottom: '20px' }} placeholder="e.g. Used Nordic Pure filters this time" value={costNote} onChange={e => setCostNote(e.target.value)} />
            <button style={S.costSaveBtn} onClick={handleSaveCost}>Save & Complete Task</button>
            <button style={S.costSkipBtn} onClick={handleSkipCost}>Skip — Complete Without Logging Cost</button>
          </div>
        </div>
      )}
    </>
  )
}